import { RecurringTypesEnum } from "dash-common";
import { formatLocalizedDate } from "dash-ui";
import { TFunction } from "i18next";
import camelCase from "lodash/camelCase";
import { SubscriptionChangeMode } from "./SubscriptionChangeConfirmationModalV2.interfaces";

// Format date as "DD Month YYYY" (e.g., "21 Oct 2025")
export const formatEndDate = (dateInput: string | Date): string => {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const day = date.getDate();
  const month = formatLocalizedDate(date, { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export const getTrialEndDate = (
  days: number,
  from: Date = new Date()
): Date => {
  const end = new Date(from);
  end.setDate(end.getDate() + days);
  return end;
};

// Get recurring type label
export const getRecurringLabel = (
  recurringType: RecurringTypesEnum,
  t: TFunction
): string => {
  return t(
    `common:subscriptionProductRecurringLabels.${camelCase(recurringType)}`
  );
};

// Get recurring period label (Annual/Monthly)
export const getRecurringPeriodLabel = (
  recurringType: RecurringTypesEnum | string,
  t: TFunction
): string => {
  if (recurringType === RecurringTypesEnum.YEAR)
    return t("pages:profilePage.billing.changeConfirm.periodAnnual");
  if (recurringType === RecurringTypesEnum.MONTH)
    return t("pages:profilePage.billing.changeConfirm.periodMonthly");
  return "";
};

export const getRecurringBillingLabel = (
  recurringType: RecurringTypesEnum | string,
  t: TFunction
): string => {
  if (recurringType === RecurringTypesEnum.YEAR)
    return t("pages:profilePage.billing.changeConfirm.billingAnnual");
  if (recurringType === RecurringTypesEnum.MONTH)
    return t("pages:profilePage.billing.changeConfirm.billingMonthly");
  return "";
};

// Get button text based on mode
export const getConfirmButtonText = (
  mode: SubscriptionChangeMode,
  totalDue: number,
  t: TFunction
): string => {
  return mode === "downgrade"
    ? t("pages:profilePage.billing.changeConfirm.confirmDowngrade")
    : t("pages:profilePage.billing.changeConfirm.confirmAndPay", {
        amount: totalDue.toFixed(2),
      });
};

// Get footer note text
export const getFooterNoteText = (
  mode: SubscriptionChangeMode,
  t: TFunction,
  endDate?: Date | string
): string => {
  if (mode === "upgrade") {
    return t("pages:profilePage.billing.changeConfirm.footerCharged");
  }
  if (endDate) {
    return t("pages:profilePage.billing.changeConfirm.footerDowngradeOn", {
      date: formatEndDate(endDate),
    });
  }
  return t("pages:profilePage.billing.changeConfirm.footerDowngradeEnd");
};

// Style constants
export const MODAL_STYLES = {
  header: {
    upgrade: {
      background: "linear-gradient(to right, #f0f7ff, #d7edff)",
      color: "#143d79",
    },
    downgrade: {
      background: "linear-gradient(to right, #fef3c7, #fde68a)",
      color: "#78350f",
    },
  },
  totalDue: {
    background: "linear-gradient(to right, #f1f5fd, #dbe4f6)",
    borderColor: "#b8c4dd",
    color: "#004c87",
  },
  button: {
    upgrade: "#337ab7",
    downgrade: "#d97706",
  },
} as const;
